import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import SalaryForkLevelsForm from './SalaryForkLevelsForm';
import useSalaryForkService, { SALARY_FORKS_CREATE, SALARY_FORKS_UPDATE } from '../useSalaryForkService';
import SalaryForkMenu from './SalaryForkMenu';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { Loading } from 'framework/ui/mui-builder';
import { useAuthorization } from 'framework/authorization';
import { useFormContext } from 'framework/form';
import { RECRUITING_CONTEXT } from '../../RecruitingContext';
import { useShowContext } from 'framework/show';
import { FloatingButtons } from 'framework/ui/ui-builder';
import { isSoftwareEngineerOrEducationalSpecialist } from 'domain/shared/employee';
import { cloneDeep } from 'lodash';

// Component to handle permission-based form submission
const FormPermissionCheck = ({ permission }: { permission: boolean }) => {
  const formContext = useFormContext();

  useEffect(() => {
    if (formContext?.setSubmitEnabled) {
      formContext.setSubmitEnabled(permission);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

const SalaryForkListLayout = () => {
  const { data } = useFormContext();
  const { setData } = useShowContext();
  const {
    id: salaryForkId,
    country,
    currency,
    specialization: selectedSpecialization,
    technology: selectedTechnology,
  } = data || {};

  const [loading, setLoading] = useState(false);
  const [isShowForm, setShowForm] = useState(false);
  const { addMessage, cleanMessage } = useMessageService();
  const { searchSalaryForksForSpec } = useSalaryForkService();
  const { isGranted } = useAuthorization();
  const canCreate = isGranted(SALARY_FORKS_CREATE, RECRUITING_CONTEXT);
  const canUpdate = isGranted(SALARY_FORKS_UPDATE, RECRUITING_CONTEXT);

  const fetchSalaryForkData = async (specializationId: string, technologyId: string | null) => {
    try {
      const salaryForks = await searchSalaryForksForSpec(specializationId, technologyId);

      setData!({
        country,
        currency,
        initialData: cloneDeep(salaryForks),
        ...salaryForks,
      });
      setShowForm(true);
    } catch (error) {
      setShowForm(false);
      addMessage(new ErrorMessage('Error fetching salary fork data.'));
    }
  };

  const handleSpecializationChange = useCallback(
    async (specializationId: string, technologyId: string | null) => {
      cleanMessage();

      try {
        await fetchSalaryForkData(specializationId, technologyId);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [country, currency],
  );

  useEffect(() => {
    if (
      !selectedSpecialization ||
      (isSoftwareEngineerOrEducationalSpecialist(selectedSpecialization) && !selectedTechnology)
    ) {
      setData!({
        country,
        currency,
        specialization: selectedSpecialization,
      });
      setShowForm(false);
      return;
    }
    if (!isSoftwareEngineerOrEducationalSpecialist(selectedSpecialization) && selectedTechnology) {
      setShowForm(false);
      return;
    }
    setLoading(true);
    handleSpecializationChange?.(selectedSpecialization, selectedTechnology);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecialization, selectedTechnology]);

  return (
    <Box sx={{ position: 'relative' }}>
      <SalaryForkMenu />
      {loading && <Loading isInitialPosition />}
      {!loading && currency && country && isShowForm && (
        <Box sx={{ width: '100%' }}>
          <SalaryForkLevelsForm />
          {salaryForkId ? (
            <FormPermissionCheck permission={canUpdate} />
          ) : (
            <FormPermissionCheck permission={canCreate} />
          )}

          <FloatingButtons isCancelButtonVisible={false} />
        </Box>
      )}
    </Box>
  );
};

export default SalaryForkListLayout;
